/**
 * PublicPageHeader — premium gradient hero header shown at the top of every public page.
 * Accepts optional recordCount to display a floating count badge.
 */

import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';

export default function PublicPageHeader({ branding = {}, recordCount = null }) {
  const { page_title, subtitle, accent_color } = branding;
  const { theme } = useTheme();
  // Use branding accent if provided, otherwise fall back to the current theme primary.
  const accentColor = (accent_color && !accent_color.startsWith('var(')) ? accent_color : theme.primary;
  const headerRef = useRef(null);

  // Trigger the entry animation once on mount
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    // Start invisible + shifted; rAF ensures paint is queued first
    el.style.opacity = '0';
    el.style.transform = 'translateY(-12px)';
    const raf = requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.55s cubic-bezier(0.4,0,0.2,1), transform 0.55s cubic-bezier(0.4,0,0.2,1)';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
    return () => cancelAnimationFrame(raf);
  }, [page_title]);

  if (!page_title && !subtitle) return null;

  // accentColor is always a concrete hex now, so no var() branching is needed.
  const heroStyle = {
    background: '#ffffff',
  };

  return (
    <div
      ref={headerRef}
      style={{
        position: 'relative',
        borderRadius: 'var(--card-radius, 12px)',
        boxShadow: `0 8px 24px ${accentColor}0c, 0 4px 10px rgba(0,0,0,0.03)`,
        overflow: 'hidden',
        padding: '1.2rem 1.6rem 1.1rem',
        marginBottom: '1rem',
        border: `1px solid ${accentColor}20`,
        ...heroStyle,
      }}
    >
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Record count badge (top-right) */}
        {recordCount != null && recordCount > 0 && (
          <div
            style={{
              position: 'absolute', top: 0, right: 0,
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: '0.72rem', fontWeight: 600,
              color: accentColor,
              background: `${accentColor}15`,
              border: `1px solid ${accentColor}30`,
              borderRadius: 'var(--button-radius, 6px)',
              padding: '4px 12px',
              letterSpacing: '0.03em',
              boxShadow: `0 2px 6px ${accentColor}10`,
            }}
          >
            <i className="bi bi-stack" style={{ fontSize: '0.65rem' }} />
            {recordCount.toLocaleString()} {recordCount === 1 ? 'record' : 'records'}
          </div>
        )}

        {page_title && (
          <h1
            style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              fontWeight: 800,
              letterSpacing: '-0.025em',
              lineHeight: 1.2,
              color: '#0f172a',
              marginBottom: subtitle ? '0.2rem' : 0,
              marginTop: 0,
            }}
          >
            {page_title}
          </h1>
        )}

        {subtitle && (
          <p
            style={{
              fontSize: '0.86rem',
              lineHeight: 1.5,
              color: '#475569',
              margin: 0,
              maxWidth: 700,
              fontWeight: 400,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* Bottom accent line */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}80, transparent)`,
          opacity: 0.85,
        }}
      />
    </div>
  );
}
